import React from 'react';
import { useNavigate } from 'react-router-dom';

const stats = [
  { icon: '🏗️', number: '100+', label: 'Years Combined Experience' },
  { icon: '🤝', number: '500+', label: 'Successful Placements' },
  { icon: '⭐', number: '98%', label: 'Client Satisfaction' },
  { icon: '🌍', number: 'UK-Wide', label: 'Coverage' },
];

const services = [
  {
    icon: '⚡',
    title: 'Temporary Recruitment',
    desc: 'Quick placements for urgent projects. Flexible workforce solutions with 24/7 support.',
    features: ['Same-day placements', 'Pre-vetted candidates', 'CSCS verified'],
  },
  {
    icon: '🎯',
    title: 'Permanent Recruitment',
    desc: 'Executive search and skilled professional placement. Graduate & apprentice programs.',
    features: ['Executive search', 'Skills assessment', 'Onboarding support'],
  },
  {
    icon: '🏗️',
    title: 'Project Solutions',
    desc: 'Complete team assembly for large-scale projects. Multi-trade coordination nationwide.',
    features: ['Full team assembly', 'Multi-trade coordination', 'Nationwide deployment'],
  },
];

const features = [
  { title: 'Construction Industry Specialists', desc: 'Deep knowledge of the construction sector' },
  { title: '24-Hour Support Available', desc: 'Round-the-clock assistance when you need it' },
  { title: 'Fast Turnaround Times', desc: 'Candidates supplied within 24 hours' },
  { title: 'Compliance & Safety Focused', desc: 'Full CSCS, right-to-work, and safety checks' },
  { title: 'Local Knowledge, National Reach', desc: 'Regional expertise with UK-wide coverage' },
  { title: 'Ethical & Transparent Process', desc: 'Honest, fair, and open at every step' },
];

const sectors = [
  { icon: '🏢', title: 'Commercial Construction', desc: 'Offices, retail & commercial projects' },
  { icon: '🏠', title: 'Residential Development', desc: 'Volume & bespoke housing projects' },
  { icon: '🌉', title: 'Infrastructure & Civil', desc: 'Highways, rail & major civil works' },
  { icon: '🏭', title: 'Industrial & Manufacturing', desc: 'Factories, plants & industrial facilities' },
  { icon: '🔧', title: 'Facilities Management', desc: 'Building maintenance & management' },
  { icon: '⚙️', title: 'Specialist Trades', desc: 'Niche skills & specialist areas' },
];

const testimonials = [
  {
    text: 'Horizon provided us with an exceptional team for our multi-million pound development. Their understanding of construction requirements is unmatched.',
    author: 'James M.',
    role: 'Project Director, Major Developer',
    initials: 'JM',
  },
  {
    text: 'Fast, reliable, and always professional. They found me a site manager role within a week. I couldn\'t be happier with the service.',
    author: 'Sarah T.',
    role: 'Site Manager',
    initials: 'ST',
  },
  {
    text: 'We\'ve worked with many agencies but Horizon truly understands the construction industry. Their compliance standards are second to none.',
    author: 'David R.',
    role: 'Managing Director, Civil Contractors',
    initials: 'DR',
  },
];

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div>
      {/* HERO */}
      <section className="hero" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            minWidth: '100%',
            minHeight: '100%',
            width: 'auto',
            height: 'auto',
            transform: 'translate(-50%, -50%)',
            zIndex: 0,
            objectFit: 'cover',
          }}
        >
          <source src="https://www.pexels.com/download/video/1197802/?fps=25.0&h=540&w=960" type="video/mp4" />
        </video>
        
        {/* Overlay for better text readability */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(26, 47, 77, 0.75)',
          zIndex: 1,
        }}></div>

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="hero-content">
            <h1>
              Building Teams That<br />
              <span className="highlight">Build the Future</span>
            </h1>
            <p>
              Expert construction recruitment connecting skilled professionals
              with leading projects across the UK. From single trades to
              complete site teams.
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/jobs')}>
                Find Your Next Role
              </button>
              <button className="btn btn-secondary btn-lg" onClick={() => navigate('/employers')}>
                Hire Top Talent
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="stats">
        <div className="container">
          <div className="stats-grid">
            {stats.map((s, i) => (
              <div className="stat-item" key={i}>
                <div className="stat-icon">{s.icon}</div>
                <div className="stat-number">{s.number}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section" style={{ paddingTop: '80px' }}>
        <div className="container">
          <div className="section-header">
            <h2>Our Recruitment Services</h2>
            <p>
              Tailored staffing solutions for the construction industry,
              from urgent temporary placements to strategic permanent hires.
            </p>
          </div>
          <div className="grid grid-3">
            {services.map((s, i) => (
              <div className="card" key={i}>
                <div className="card-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <ul className="card-list">
                  {s.features.map((f, j) => (
                    <li key={j}>{f}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <button className="btn btn-outline" onClick={() => navigate('/services')}>
              View All Services →
            </button>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section-alt">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose Horizon?</h2>
            <p>
              Construction recruitment specialists who understand your
              industry, your deadlines, and your quality standards.
            </p>
          </div>
          <div className="grid grid-2">
            {features.map((f, i) => (
              <div className="feature-item" key={i}>
                <div className="feature-check">✓</div>
                <div>
                  <h4>{f.title}</h4>
                  <p>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Sectors We Serve</h2>
            <p>
              Specialist recruitment across all major construction sectors,
              from commercial builds to specialist trades.
            </p>
          </div>
          <div className="grid grid-3">
            {sectors.map((s, i) => (
              <div
                className="sector-card"
                key={i}
                onClick={() => navigate('/sectors')}
              >
                <div className="sector-icon">{s.icon}</div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-alt">
        <div className="container">
          <div className="section-header">
            <h2>What Our Clients Say</h2>
            <p>Trusted by leading construction companies across the UK.</p>
          </div>
          <div className="grid grid-3">
            {testimonials.map((t, i) => (
              <div className="testimonial-card" key={i}>
                <p className="testimonial-text">{t.text}</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">{t.initials}</div>
                  <div className="testimonial-info">
                    <h5>{t.author}</h5>
                    <p>{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="container">
          <h2>Ready to Build Your Team?</h2>
          <p>Contact us today for tailored recruitment solutions.</p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-lg" onClick={() => navigate('/contact')}>
              Contact Us
            </button>
            <button className="btn btn-dark btn-lg" onClick={() => navigate('/employers')}>
              Request Staff
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
